// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import translate from "@insite/client-framework/Translate";
import { DataTableCell, GridContainer, GridItem, Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { InvoiceDetailModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isInvoiceDetail } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import {
    BillableTreatments,
    InvoiceDetailParameters,
    InvoiceSummaryData,
    SourceType,
} from "@store/Framework/Types/types";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import { ButtonReport } from "@store/Library/Components/Reporting/Common/ApiDataModal";
import CompanyCard from "@store/Library/Components/Reporting/Common/CompanyCard";
import { ProductHeadingTypography } from "@store/Library/Components/Reporting/Common/ProductSection";
import InvoiceDetailModal from "@store/Library/Components/Reporting/InvoiceDetail/InvoiceDetailModal";
import {
    CompanyInvoiceSummaries,
    invoiceSummaryHeaders,
    invoiceSummaryKeys,
} from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import uniqueId from "lodash/uniqueId";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";

const mobileQuery = "@media (max-width: 767px)";
const CompanyHeader = styled(Typography)`
    font-weight: bold;
    font-size: 2rem;
    width: 100%;
    color: ${getColor("primary")};
    border-radius: 0.5rem;
    margin-bottom: 2rem;
    margin-top: 2rem;
    text-align: center;
`;

export const getTotals = (data: InvoiceSummaryData[]) => {
    return data.reduce(
        (acc, curr) => {
            acc.subtotal += curr.itemstotal;
            acc.totalTax += curr.gst;
            acc.ordertotal += curr.ordertotal;
            acc.totalfreightcharges += curr.freightcharges;
            return acc;
        },
        { subtotal: 0, totalTax: 0, ordertotal: 0, totalfreightcharges: 0 },
    );
};

const StyledGridItem = styled(GridItem)`
    display: flex;
    flex-direction: column;
    overflow-x: auto;
`;

interface Props {
    companyInvoiceSummaries: CompanyInvoiceSummaries;
    billableTreatment: BillableTreatments;
    sourceType: SourceType;
}

const InvoiceSummaryDisplay: React.FC<Props> = ({ companyInvoiceSummaries, billableTreatment, sourceType }) => {
    const companyIds = Object.keys(companyInvoiceSummaries);
    const [openModel, setOpenModel] = React.useState<number>();
    const { isLoading, updateData } = useReportApi("invoice");
    const localizeCurrency = useLocalizeCurrency();
    const [invoiceDetails, setInvoiceDetails] = React.useState<{
        [invoiceNum: number]: [InvoiceSummaryData, InvoiceDetailModel];
    }>({});

    const handleModalOpen = async (invoiceSummary: InvoiceSummaryData) => {
        const invoiceNum = invoiceSummary.invoicenum;
        const invoiceDetailParameters: InvoiceDetailParameters = {
            invoiceNum,
            billableTreatment,
            sourceType,
        };
        setOpenModel(invoiceNum);
        if (invoiceDetails[invoiceNum] !== undefined) {
            return;
        }

        const data = await updateData(invoiceDetailParameters);
        if (isInvoiceDetail(data)) {
            setInvoiceDetails(prevState => ({ ...prevState, [invoiceNum]: [invoiceSummary, data] }));
        }
    };

    const getTableData = (data: InvoiceSummaryData[]): DataTableData<InvoiceSummaryData> => {
        const dataWithElements = data.map(invoiceSummary => {
            const invoiceNum = invoiceSummary.invoicenum;
            const element = (
                <DataTableCell
                    key={invoiceSummary.invoicenum}
                    css={css`
                        display: flex;
                        justify-content: center;
                    `}
                >
                    <ButtonReport
                        isLoading={isLoading && openModel === invoiceNum}
                        onClick={() => handleModalOpen(invoiceSummary)}
                    >
                        {translate("View Order Items")}
                    </ButtonReport>
                </DataTableCell>
            );
            return { ...invoiceSummary, extraElement: element };
        });
        return {
            title: `${data[0].customername}Invoice Summaries`,
            headers: invoiceSummaryHeaders,
            dataKeys: invoiceSummaryKeys,
            data: dataWithElements,
            dataSummaryTitle: translate("invoices"),
        };
    };
    const formatDictionary: FormatDictionary = {
        itemstotal: localizeCurrency,
        gst: localizeCurrency,
        freightcharges: localizeCurrency,
        ordertotal: localizeCurrency,
    };
    const activeInvoice = openModel ? invoiceDetails[openModel] : undefined;
    const [activeInvoiceSummary, activeInvoiceDetail] = activeInvoice || [undefined, undefined];
    return (
        <>
            <InvoiceDetailModal
                invoiceDetails={activeInvoiceDetail}
                invoiceSummary={activeInvoiceSummary}
                isOpen={!!openModel}
                invoiceSummaryDictionary={formatDictionary}
                handleClose={() => setOpenModel(undefined)}
            />
            {companyIds.map(companyId => {
                const companyName = companyInvoiceSummaries[companyId].name;
                const customers = Object.values(companyInvoiceSummaries[companyId].locations);
                return (
                    <GridContainer
                        key={uniqueId()}
                        offsetCss={css`
                            justify-content: center;
                        `}
                    >
                        <GridItem width={12}>
                            <CompanyHeader>{companyName}</CompanyHeader>
                        </GridItem>
                        {customers.map(customer => {
                            const { subtotal, totalTax, ordertotal, totalfreightcharges } = getTotals(customer);

                            return (
                                <Fragment key={companyName}>
                                    <GridItem width={12}>
                                        <CompanyCard>
                                            <ProductHeadingTypography>{`${customer[0].customername}`}</ProductHeadingTypography>
                                            <ProductHeadingTypography>
                                                {`${translate("Total Freight:")} ${localizeCurrency(
                                                    totalfreightcharges,
                                                )}`}
                                            </ProductHeadingTypography>
                                            <ProductHeadingTypography>
                                                {`${translate("Subtotal:")} ${localizeCurrency(subtotal)}`}
                                            </ProductHeadingTypography>
                                            <ProductHeadingTypography>
                                                {`${translate("Tax:")} ${localizeCurrency(totalTax)}`}
                                            </ProductHeadingTypography>
                                            <ProductHeadingTypography>
                                                {`${translate("Order Total:")} ${localizeCurrency(ordertotal)}`}
                                            </ProductHeadingTypography>
                                        </CompanyCard>
                                    </GridItem>
                                    <StyledGridItem width={12}>
                                        <ReportDataTable
                                            dataTableData={{
                                                ...getTableData(customer),
                                                formatDictionary,
                                            }}
                                        />
                                    </StyledGridItem>
                                </Fragment>
                            );
                        })}
                    </GridContainer>
                );
            })}
        </>
    );
};

export default InvoiceSummaryDisplay;
